import { fetchAuthSession } from 'aws-amplify/auth';
import type { ImportBlanks, ImportMapping, RowPiece, SheetLocation } from './importFlow';
import type { Cell } from './spreadsheet';
import type { LocationRowResult } from './locationImport';

/**
 * Thin client for the vendor-api (glplus-vendor-api). Attaches the Cognito id
 * token as a Bearer credential; the vendor-api re-verifies it + re-checks the
 * caller's per-vendor capability server-side (UI gating is cosmetic).
 *
 * Auth model: a user is in the single `vendor` Cognito group; their ROLE is
 * per-vendor (from VendorMembership). So every scoped call threads a `vendorId`
 * — the portal resolves the active vendor from `/vendor/me/capabilities`.
 *
 * NEVER surfaces UPC — products are handled by name + GLPC id
 * (feedback_never_show_upc).
 */
const BASE = import.meta.env.VITE_VENDOR_API_URL as string | undefined;

export class VendorApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** A stable key for errors the portal words itself, in the reader's language. */
    public readonly code?: string,
    /** When the error is about one size row of "Add a product": its position. */
    public readonly sizeIndex?: number,
    /** When the error is about one tab of a spreadsheet: its position. */
    public readonly sheetIndex?: number,
  ) {
    super(message);
    this.name = 'VendorApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!BASE) throw new VendorApiError('VITE_VENDOR_API_URL is not configured', 0);
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const body = payload as { error?: string; code?: string; sizeIndex?: number; sheetIndex?: number } | null;
    throw new VendorApiError(body?.error ?? `Request failed (${res.status})`, res.status, body?.code, body?.sizeIndex, body?.sheetIndex);
  }
  return (payload as { data: T }).data;
}

async function download(path: string): Promise<{ blob: Blob; filename: string }> {
  if (!BASE) throw new VendorApiError('VITE_VENDOR_API_URL is not configured', 0);
  const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const payload = await res.json() as { error?: string };
      message = payload.error ?? message;
    } catch {
      // A binary endpoint may return an empty error body.
    }
    throw new VendorApiError(message, res.status);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = disposition.match(/filename="([^"]+)"/i)?.[1];
  const plain = disposition.match(/filename=([^;]+)/i)?.[1]?.trim();
  return {
    blob: await res.blob(),
    filename: encoded ? decodeURIComponent(encoded) : quoted ?? plain ?? 'download',
  };
}

const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ─── Types (mirror the live vendor-api contract) ─────────────────────────────

export type VendorRole = 'owner' | 'manager' | 'clerk';

export interface VendorMembershipInfo {
  vendorId: string;
  glpVendorId: string | null;
  businessName: string;
  role: VendorRole;
  capabilities: string[];
}

export type ApplicationStatus =
  | 'submitted'
  | 'in_review'
  | 'data_shape_assigned'
  | 'staff_visit_scheduled'
  | 'adapter_built'
  | 'vendor_confirmed'
  | 'approved'
  | 'declined'
  | 'withdrawn';

export interface SocialProfile {
  platform: string;
  handle: string;
}

export interface ApplicationAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

/** Per-day store hours; an absent day or `closed` reads as closed. */
export interface DayHours {
  open?: string;
  close?: string;
  closed?: boolean;
}
export type StoreHours = Record<string, DayHours>;

/**
 * The `businessInfo` JSON contract written by the §1B apply form and read by the
 * admin review console (mirror of buildBusinessInfo in glplus-vendor-api). All
 * keys optional — older minimal applications only carry name/type/address.
 */
export interface ApplicationBusinessInfo {
  businessName?: string;
  businessType?: string;
  businessTypeOther?: string;
  contactName?: string;
  role?: string;
  contactPhone?: string;
  contactEmail?: string;
  website?: string;
  socialProfiles?: SocialProfile[];
  numberOfLocations?: string;
  address?: ApplicationAddress;
  snapAccepted?: boolean;
  fnsNumber?: string;
  approxProductCount?: string;
  interests?: string[];
  interestsOther?: string;
}

export interface VendorApplication {
  applicationId: string;
  status: ApplicationStatus;
  businessName: string | null;
  businessType: string | null;
  businessInfo: ApplicationBusinessInfo;
  hours: StoreHours | null;
  posSystem: string | null;
  categories: unknown;
  motivation: string | null;
  declinedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitApplicationInput {
  businessName: string;
  businessType: string;
  businessTypeOther?: string;
  contactName?: string;
  role?: string;
  contactPhone?: string;
  website?: string;
  socialProfiles?: SocialProfile[];
  numberOfLocations?: string;
  address: ApplicationAddress;
  hours?: StoreHours;
  snapAccepted?: boolean;
  fnsNumber?: string;
  posSystem?: string;
  approxProductCount?: string;
  interests?: string[];
  interestsOther?: string;
  categories?: string[];
  motivation?: string;
}

export interface MeCapabilities {
  /** tblvendor_users id — the vendor-pool identity (pool-per-actor, 2026-09-17). */
  vendorUserId: string;
  email: string;
  memberships: VendorMembershipInfo[];
  /** The caller's current vendor application, or null. */
  application: VendorApplication | null;
}

// ─── Products — what it is is locked; what it costs is the vendor's, per location ─

export type ProductStatus =
  | 'needs_answer'
  | 'with_us'
  | 'stuck'
  | 'hidden'
  | 'not_sold_here'
  | 'location_pending'
  | 'verifying'
  | 'returned'
  | 'live';

export type ProductFilter =
  | 'all'
  | 'live'
  | 'needs_answer'
  | 'with_us'
  | 'stuck'
  | 'verifying'
  | 'returned'
  | 'hidden'
  | 'on_sale'
  | 'out_of_stock'
  | 'recalled';

/**
 * `assigned` = the barcode we gave an item that had none. `own` = the
 * manufacturer barcode of a product only this vendor sells. `shared` = a
 * barcode other stores' products carry too; it is never sent, the GLPC is
 * shown instead.
 */
export type ProductBarcode =
  | { kind: 'assigned'; value: string }
  | { kind: 'own'; value: string }
  | { kind: 'shared'; value: null }
  | { kind: 'none'; value: null };

export interface ProductLocationRef {
  storeId: string;
  name: string;
  status: LocationStatus | null;
}

export interface ProductListItem {
  vendorProductId: string;
  glpProductId: string;
  name: string;
  brand: string | null;
  size: string | null;
  imageUrl: string | null;
  department: string;
  category: string;
  vendorSku: string | null;
  barcode: ProductBarcode;
  recalled: boolean;
  status: ProductStatus;
  priceCents: number | null;
  salePriceCents: number | null;
  /** The sale's first day at the store, `YYYY-MM-DD`. */
  saleStartsOn: string | null;
  /** The sale's last day at the store, `YYYY-MM-DD`. null = no end date. */
  saleEndsOn: string | null;
  inStock: boolean;
}

export interface ProductsListResponse {
  /** The location the prices and statuses are for. null = the vendor has no location yet. */
  location: ProductLocationRef | null;
  locations: ProductLocationRef[];
  counts: Record<ProductFilter, number>;
  departments: string[];
  page: number;
  pageSize: number;
  total: number;
  items: ProductListItem[];
}

export interface ProductLocationPrice extends ProductLocationRef {
  priceCents: number | null;
  salePriceCents: number | null;
  saleStartsOn: string | null;
  saleEndsOn: string | null;
  inStock: boolean;
  shown: boolean;
}

export type ChangeField =
  | 'name'
  | 'department_category'
  | 'size'
  | 'description_short'
  | 'description_long'
  | 'ingredients'
  | 'nutrition_label'
  | 'photo'
  | 'name_es'
  | 'description_short_es'
  | 'description_long_es'
  | 'ingredients_es'
  | 'other';

export interface ChangeRequest {
  requestId: string;
  field: ChangeField;
  currentValue: string | null;
  proposedValue: string | null;
  reason: string | null;
  attachmentUrl: string | null;
  state: 'open' | 'approved' | 'rejected' | 'withdrawn';
  /** What we told the vendor when we decided it. */
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface ProductSpanish {
  itemNameEs: string | null;
  descriptionShortEs: string | null;
  descriptionLongEs: string | null;
  ingredientsTextEs: string | null;
  /** false = changing a value that already exists is sent for review */
  direct: boolean;
}

export interface ProductDetail {
  vendorProductId: string;
  glpProductId: string;
  vendorSku: string | null;
  barcode: ProductBarcode;
  blockState: 'questioned' | 'handed_to_glp' | 'unresolvable' | null;
  recalled: boolean;
  info: {
    name: string;
    brand: string | null;
    department: string;
    category: string;
    subCategory: string | null;
    size: string | null;
    descriptionShort: string | null;
    descriptionLong: string | null;
    ingredients: string | null;
    imageUrl: string | null;
    imageIsVendors: boolean;
    nutritionOnFile: boolean;
  };
  spanish: ProductSpanish;
  locations: ProductLocationPrice[];
  sizes: Array<{ vendorProductId: string; name: string; size: string | null; assignedBarcode: string | null }>;
  changeRequests: ChangeRequest[];
  /** Why it is hidden everywhere and what we asked — null when nothing is in the way. */
  block: ProductBlock | null;
  /** Our staff's check of a new product: waiting, approved, or sent back with a note. */
  review: ProductReview;
}

export interface ProductReview {
  status: 'pending' | 'approved' | 'returned';
  note: string | null;
}

// ─── Adding a product by hand ────────────────────────────────────────────────

export type SizeUnit = 'oz' | 'lb' | 'g' | 'kg' | 'fl oz' | 'ml' | 'l' | 'pt' | 'qt' | 'gal' | 'ct';

/** "I don't have one" is an answer; leaving it out is not. */
export type AttachmentAnswer = { kind: 'upload'; url: string } | { kind: 'none' };

export interface CreateProductsInput {
  name: string;
  /** Who makes it, as the vendor writes it. */
  brand?: string;
  department: string;
  category: string;
  descriptionShort: string;
  descriptionLong?: string;
  ingredients?: string;
  photo: AttachmentAnswer;
  nutritionLabel: AttachmentAnswer;
  servingsPerContainer?: number;
  spanish?: { itemNameEs?: string; descriptionShortEs?: string };
  sizes: Array<{
    sizeValue: number;
    sizeUnit: SizeUnit;
    barcode: { kind: 'value'; value: string } | { kind: 'none' };
    vendorSku?: string;
    priceCents: number;
    locations: 'all' | string[];
  }>;
  /** "Add a size" from a product page: the new sizes join that product's family. */
  sizeOfVendorProductId?: string;
}

export interface CreatedProduct {
  /** added = live where its location is; needs_answer = hidden until a question is answered. */
  status: 'added' | 'needs_answer' | 'already_yours';
  vendorProductId: string;
  glpProductId: string;
  /** Set when the product had no barcode and we gave it one. */
  assignedBarcode: string | null;
  sizeValue: number;
  sizeUnit: string;
}

// ─── Questions about a product ───────────────────────────────────────────────

/** What the vendor told us about the product. The barcode is the one THEY typed. */
export interface SentProduct {
  name: string;
  size: string | null;
  department: string | null;
  category: string | null;
  descriptionShort: string | null;
  ingredients: string | null;
  hasPhoto: boolean;
  barcode: string | null;
  noBarcode: boolean;
  source: string;
  /** The spreadsheet row it came from; null when it was typed in. */
  rowNumber: number | null;
  /** The file and tab that row is in; null when it was typed in. */
  file?: string | null;
  sheet?: string | null;
  addedAt: string;
}

/** Our one best guess at what it is. Its barcode is never sent. */
export interface SuggestedProduct {
  glpProductId: string;
  name: string;
  brand: string | null;
  size: string | null;
  department: string | null;
  category: string | null;
  ingredients: string | null;
  imageUrl: string | null;
}

/** `staff_proposal`: the vendor handed it to us and we think it is this product. */
export type QuestionReason = 'barcode_name_differs' | 'barcode_details_differ' | 'name_similar' | 'staff_proposal';

export interface ProductQuestion {
  questionId: string;
  vendorProductId: string;
  kind: string;
  reason: QuestionReason | string | null;
  field: string | null;
  state: 'open' | 'handed_to_glp' | 'unresolvable' | 'resolved';
  suggested: SuggestedProduct | null;
  /** The vendor's barcode is the suggested product's barcode. */
  barcodeMatches: boolean;
  /** Our product's barcode beside theirs — the barcode itself is never sent. */
  suggestedBarcode?: 'same' | 'different' | 'none' | null;
  /** What we wrote when we proposed the product. */
  note?: string | null;
  askedAt: string;
  handedAt: string | null;
  stuckReason: string | null;
  stuckAt: string | null;
}

/** What we still need to know about a product before shoppers may see it. */
export type MissingField = 'size' | 'department_category' | 'description_short' | 'ingredients' | 'photo' | 'nutrition_label';

/** One product waiting on the vendor: a yes/no about what it is, or a list of what is missing. */
export interface QuestionListItem extends ProductQuestion {
  sent: SentProduct | null;
  missing: MissingField[];
}

export interface SupplyInput {
  size?: { sizeValue: number; sizeUnit: SizeUnit };
  department?: string;
  category?: string;
  descriptionShort?: string;
  ingredients?: string;
  photo?: AttachmentAnswer;
  nutritionLabel?: AttachmentAnswer;
}

export interface QuestionsListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: QuestionListItem[];
}

export interface ProductBlock {
  state: 'questioned' | 'handed_to_glp' | 'unresolvable';
  sent: SentProduct | null;
  questions: ProductQuestion[];
  missing: MissingField[];
  history: Array<{ at: string; event: 'asked' | 'answered' | 'handed' | 'stuck' }>;
}

export interface AnswerResult {
  questionId: string;
  vendorProductId: string;
  blockState: ProductBlock['state'] | null;
  outcome: 'confirmed' | 'created_new' | 'handed_to_glp';
  assignedBarcode: string | null;
  /** After "no, it is my own": what their file had not told us about it. */
  missing: MissingField[];
}

// ─── Spreadsheet import ──────────────────────────────────────────────────────

export interface ImportSheetInput {
  file: string;
  sheet: string;
  headers: string[];
  rowCount: number;
}

export interface ImportPreview {
  locations: Array<{ storeId: string; name: string }>;
  sheets: Array<{ file: string; sheet: string; mapping: ImportMapping; location: SheetLocation; remembered: boolean }>;
  blanks: ImportBlanks;
  maxRows: number;
}

export interface ImportStatus {
  batchId: string;
  status: 'uploading' | 'working' | 'done' | 'failed';
  files: string[];
  startedAt: string;
  total: number;
  processed: number;
  live: number;
  needsAnswer: number;
  unreadable: number;
}

export type ImportOutcome = 'live' | 'needs_answer' | 'unreadable';

export interface ImportResultRow {
  rowId: string;
  file: string | null;
  sheet: string | null;
  sheetIndex: number;
  rowNumber: number;
  name: string;
  size: string | null;
  /** Why the row was sent back; null for a row that was taken in. */
  problem: { code: string; value?: string } | null;
  vendorProductId: string | null;
  blockState: string | null;
  cells?: Cell[];
}

export interface ImportRowsResponse {
  page: number;
  pageSize: number;
  total: number;
  headers?: Array<{ file: string; sheet: string; headers: string[] }>;
  items: ImportResultRow[];
}

export interface ReopenInput {
  barcode?: { kind: 'value'; value: string } | { kind: 'none' };
  photoUrl?: string;
  note?: string;
}

export interface LocationPriceInput {
  storeId: string;
  /** null = not sold at this location */
  priceCents: number | null;
  salePriceCents: number | null;
  /** null = today, or the start a sale already had */
  saleStartsOn: string | null;
  saleEndsOn: string | null;
  inStock: boolean;
  shown: boolean;
}

export type BulkAction =
  | { type: 'set_price'; priceCents: number }
  | { type: 'adjust_price'; percent: number }
  | { type: 'put_on_sale'; percentOff?: number; salePriceCents?: number }
  | { type: 'end_sale' }
  | { type: 'hide' }
  | { type: 'show' }
  | { type: 'in_stock' }
  | { type: 'out_of_stock' };

export type BulkSkipReason = 'not_sold_here' | 'sale_not_below_price' | 'not_on_sale' | 'not_found';

export interface BulkResult {
  updated: number;
  unchanged: number;
  skipped: Array<{ vendorProductId: string; reason: BulkSkipReason }>;
}

export interface ProductSpanishInput {
  itemNameEs?: string;
  descriptionShortEs?: string;
  descriptionLongEs?: string;
  ingredientsTextEs?: string;
}

export type AssetKind = 'logo' | 'photo' | 'product-photo' | 'label-photo' | 'request-attachment';

export interface VendorProfile {
  vendorId: string;
  glpVendorId: string | null;
  businessName: string;
  businessType: string;
  posSystem: string;
  address: unknown;
  hours: unknown;
  categories: unknown;
  photosJson: StorefrontPhotos | null;
  snapAuthorized: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Storefront image metadata (§9 wizard Storefront step), stored in the Vendor
 * `photosJson` Json column. Legacy description/certification values remain
 * readable, but new certification evidence uses the reviewed certification API.
 */
export interface StorefrontPhotos {
  logo?: string;
  photos?: string[];
  description?: string;
  certifications?: string[];
}

export interface UpdateProfileInput {
  hours?: unknown;
  categories?: unknown;
  photosJson?: StorefrontPhotos;
}

export type DashboardWidget =
  | 'orders'
  | 'payouts'
  | 'needs_answer'
  | 'quick_price'
  | 'sales_ending'
  | 'circular'
  | 'feed';

export interface DashboardPreferences {
  version: 1;
  order: DashboardWidget[];
  hidden: DashboardWidget[];
  persisted: boolean;
}

export type VendorUserStatus = 'invited' | 'active' | 'disabled';

export interface TeamMember {
  membershipId: string;
  role: VendorRole;
  createdAt: string;
  vendorUserId: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  /** invited = has not signed in yet (Cognito emailed the invitation). */
  status: VendorUserStatus | null;
}

/** Invite a teammate — provisions their vendor-pool account. */
export interface AddMemberInput {
  email: string;
  firstName: string;
  lastName: string;
  role: VendorRole;
}

// ─── Locations (Phase 1) — a location is a Store row with vendorId ──────────

export type LocationStatus = 'verifying' | 'live' | 'needs_changes';
export type FnsVerification = 'unverified' | 'verified' | 'failed';

export interface LocationAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  formatted: string;
}

export interface LocationDayHours {
  open?: string;
  close?: string;
  closed: boolean;
}

export interface VendorLocation {
  storeId: string;
  name: string;
  address: LocationAddress;
  latitude: number;
  longitude: number;
  phoneE164: string | null;
  website: string | null;
  hours: Record<string, LocationDayHours>;
  pickupSupported: boolean;
  /** Shopper delivery is derived from Runner coverage and is not vendor-editable. */
  deliveryCoverageDerived: boolean;
  temporarilyClosed: boolean;
  /** Shown to shoppers only once the FNS number is verified (§14). */
  snapAccepted: boolean;
  fnsNumber: string | null;
  fnsVerification: FnsVerification | null;
  fnsVerifiedAt: string | null;
  status: LocationStatus | null;
  /** The reviewer's reason when status is needs_changes — shown verbatim. */
  statusNote: string | null;
  source: 'manual' | 'import' | 'api' | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  productsPriced?: number;
  onSaleNow?: number;
  launchChecklist: {
    profileAddress: boolean;
    hours: boolean;
    compliance: boolean;
    fulfillment: boolean;
    products: boolean;
  };
  setupComplete: boolean;
}

export interface LocationsListResponse {
  headquarters: { businessName: string; address: unknown };
  counts: { all: number; live: number; verifying: number; needs_changes: number };
  items: VendorLocation[];
}

// ── Circulars ─────────────────────────────────────────────────────
export type CircularIssueStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'published' | 'expired' | 'withdrawn';
export type CircularOfferType = 'fixed_price' | 'bogo' | 'multi_buy';
export interface CircularConfig {
  cadence: { frequency: 'biweekly'; publicationWeekday: 'Wednesday'; displayDays: number };
  cutoff: { weekday: 'Tuesday'; localTime: string; daysBeforePublication: number };
  departments: string[];
  templates: string[];
  offerLimits: { storedPerDepartment: number; displayedPerDepartment: number; featuredPerDepartment: number; productsPerOffer: number };
  xlsxColumns: string[];
  circularImages: { maximumActivePerVendorProduct: number; maximumBytes: number; contentTypes: string[]; packagingAllowed: boolean; approvalRequired: boolean };
}
export interface CircularIssueSummary {
  issueId: string;
  publicationDate: string;
  displayStartsOn: string;
  displayEndsOn: string;
  cutoffAt: string;
  businessTimezone: string;
  templateKey: string;
  locationScope: 'all' | 'selected';
  locationsResolvedAt: string | null;
  status: CircularIssueStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  withdrawnAt: string | null;
  locations: Array<{ storeId: string; resolutionSource: string }>;
  pdfs: Array<{ pdfId?: string; storeId: string; locale: 'en' | 'es'; status: string; cdnUrl: string | null }>;
  offerCounts: Record<string, { stored: number; displayed: number; featured: number }>;
}
export interface CircularImage {
  imageId: string;
  vendorProductId: string;
  glpProductId: string;
  cdnUrl: string | null;
  status: 'uploading' | 'pending' | 'approved' | 'rejected' | 'archived';
  rejectionReason: string | null;
  contentType: string;
  byteSize: number;
}
export interface CircularOfferProduct {
  offerProductId: string;
  vendorProductId: string;
  glpProductId: string;
  productNameSnapshot: string;
  brandSnapshot: string | null;
  circularImageId: string | null;
  circularImage: { imageId: string; cdnUrl: string | null; status: string; rejectionReason: string | null } | null;
  vendorProduct: { product: { primaryImageUrl: string | null }; circularImages: Array<{ imageId: string; cdnUrl: string | null; status: string; rejectionReason: string | null }> };
}
export interface CircularIssueDetail extends CircularIssueSummary {
  offers: Array<{
    issueOfferId: string;
    departmentKey: string;
    displaySelected: boolean;
    featured: boolean;
    sortOrder: number;
    offerSnapshot: Record<string, unknown>;
    products: CircularOfferProduct[];
    locations: Array<{ storeId: string; store: { storeId: string; name: string } }>;
  }>;
  reviews: Array<{ reviewId: string; action: string; reason: string | null; createdAt: string }>;
}
export type CircularPromotionInput = {
  sourceKey?: string;
  glpProductIds: string[];
  offerType: CircularOfferType;
  startsOn?: string;
  endsOn: string;
  fixedPriceCents?: number;
  buyQuantity?: number;
  getQuantity?: number;
  multiQuantity?: number;
  multiPriceCents?: number;
  headlineEn?: string;
  headlineEs?: string;
  termsEn?: string;
  termsEs?: string;
  locations: { kind: 'all' } | { kind: 'selected'; storeIds: string[] };
};

export interface LocationAddressInput {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface CreateLocationInput {
  name: string;
  address: LocationAddressInput;
  phoneE164?: string;
  website?: string;
  hours?: Record<string, LocationDayHours>;
  pickupSupported?: boolean;
  fnsNumber?: string;
}

export interface UpdateLocationInput {
  name?: string;
  address?: LocationAddressInput;
  phoneE164?: string | null;
  website?: string | null;
  hours?: Record<string, LocationDayHours> | null;
  pickupSupported?: boolean;
  temporarilyClosed?: boolean;
  fnsNumber?: string | null;
}

export interface LocationArchive {
  archiveId: string;
  storeId: string;
  snapshot: Record<string, unknown>;
  archiveReason: string | null;
  archivedAt: string;
}

export interface CertificationType {
  key: string;
  label: string;
}

export interface VendorCertification {
  certificationId: string;
  storeId: string;
  certificationKey: string;
  customLabel: string | null;
  issuerName: string;
  certificateRef: string | null;
  issuedOn: string | null;
  expiresOn: string;
  status: 'pending' | 'verified' | 'rejected' | 'expired';
  displayed: boolean;
  proofFileName: string;
  proofContentType: string;
  proofByteSize: number;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

export interface SupportContact {
  contactId: string;
  name: string;
  role: string | null;
  email: string | null;
  phoneE164: string | null;
  notifyFeedFailures: boolean;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OwnershipRequest {
  requestId: string;
  requestType: 'portal_owner_transfer' | 'legal_ownership_change';
  targetVendorUserId: string | null;
  details: Record<string, unknown>;
  status: 'submitted' | 'needs_information' | 'approved' | 'declined' | 'cancelled';
  reviewNote: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  cancelledAt: string | null;
  targetUser: { email: string; givenName: string | null; familyName: string | null } | null;
}

export const RECIPIENT_EMAIL_TYPES = [
  'location_decision',
  'questions_waiting',
  'import_finished',
  'change_request_decision',
  'product_review_decision',
] as const;
export type RecipientEmailType = (typeof RECIPIENT_EMAIL_TYPES)[number];

export interface LocationRecipient {
  emailType: RecipientEmailType;
  email: string;
  name: string | null;
  vendorUserId: string | null;
}

export interface LocationRecipients {
  maxPerType: number;
  types: Record<RecipientEmailType, LocationRecipient[]>;
}

export type RecipientInput = { vendorUserId: string } | { email: string; name?: string };

export interface ImportOriginalUpload {
  fileId: string;
  name: string;
  uploadUrl: string;
  contentType: string;
}

export interface VendorRecall {
  glpProductId: string;
  productName: string | null;
  title: string;
  classification: string;
  recallDate: string;
  source: string;
}

export type SaleEndingFilter = 'any' | 'seven_days' | 'thirty_days' | 'no_end';

export interface PromotionSale {
  vendorProductId: string;
  glpProductId: string;
  name: string;
  brand: string | null;
  vendorSku: string | null;
  location: ProductLocationRef;
  priceCents: number;
  salePriceCents: number;
  saleStartsOn: string | null;
  saleEndsOn: string | null;
  state: 'active' | 'scheduled';
  visibleToShoppers: boolean;
}

export interface PromotionSalesResponse {
  locations: ProductLocationRef[];
  page: number;
  pageSize: number;
  total: number;
  items: PromotionSale[];
}

export interface ReportsOverview {
  generatedAt: string;
  scope: { storeId: string; name: string } | null;
  locations: Array<{ storeId: string; name: string; status: string }>;
  catalog: {
    total: number;
    approved: number;
    pendingReview: number;
    returned: number;
    needsAnswer: number;
    withGlp: number;
    stuck: number;
  };
  listings: {
    priced: number;
    live: number;
    hidden: number;
    outOfStock: number;
    onSale: number;
  };
  workflow: {
    questionsResolved30d: number;
    changeRequestsOpen: number;
    changeRequestsDecided30d: number;
  };
  priceActivity: {
    changes7d: number;
    changes30d: number;
    lastChangedAt: string | null;
  };
  recalls: { active: number };
  imports: Array<{
    batchId: string;
    source: string;
    status: string;
    originalFilename: string | null;
    rowCount: number;
    committedCount: number;
    questionedCount: number;
    rejectedCount: number;
    createdAt: string;
    updatedAt: string;
  }>;
  adapters: Array<{
    adapterId: string;
    type: string;
    status: string;
    cadenceMinutes: number | null;
    lastRunAt: string | null;
    lastRunSuccess: boolean | null;
    nextRunAt: string | null;
    latestRun: {
      startedAt: string;
      completedAt: string | null;
      success: boolean | null;
      productsUpdated: number;
      productsAdded: number;
      productsRemoved: number;
    } | null;
  }>;
}

// ─── Vendor-prepared orders and finance ─────────────────────────────────────

export type VendorOrderPhase = 'preliminary' | 'final' | 'preparing' | 'ready' | 'handed_off';

export interface VendorRunnerCard {
  firstName: string | null;
  lastInitial: string | null;
  photoUrl: string | null;
  verificationState: string | null;
  arrivalState: string | null;
  vehicle: { mode: string; make: string | null; model: string | null; color: string | null; photoUrl: string | null } | null;
}

export interface VendorOrderSummary {
  orderLocationId: string;
  glpOrderId: string | null;
  bagCode: string;
  location: { storeId: string; name: string; timezone: string | null };
  phase: VendorOrderPhase;
  isFinal: boolean;
  fulfillmentStatus: string;
  runDate: string | null;
  deliveryWindowStart: string | null;
  deliveryWindowEnd: string | null;
  cutoffAt: string | null;
  itemCount: number;
  lineCount: number;
  exceptionCount: number;
  bagCount: number | null;
  notifiedAt: string | null;
  prepStartedAt: string | null;
  readyAt: string | null;
  handedOffAt: string | null;
  runner: VendorRunnerCard | null;
}

export interface VendorOrdersResponse {
  page: number;
  pageSize: number;
  total: number;
  summary: Record<VendorOrderPhase, number>;
  locations: Array<{ storeId: string; name: string }>;
  items: VendorOrderSummary[];
}

export interface VendorOrderItem {
  orderItemId: string;
  glpProductId: string;
  name: string;
  size: string | null;
  quantity: number;
  unitPriceCents: number;
  isWeighed: boolean;
  lineStatus: string;
  substituteName: string | null;
  finalQuantity: number | null;
  finalUnitPriceCents: number | null;
  note: string | null;
  imageUrl: string | null;
  checked: boolean;
  unavailable: boolean;
}

export interface VendorOrderDetail extends Omit<VendorOrderSummary, 'itemCount' | 'lineCount' | 'exceptionCount'> {
  items: VendorOrderItem[];
  evidence: Array<{ evidenceId: string; type: string; url: string | null; capturedAt: string }>;
}

export interface VendorPayoutSummary {
  payoutId: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  amountCents: number;
  grossSalesCents: number;
  commissionCents: number;
  adjustmentCents: number;
  orderCount: number;
  finalizedAt: string | null;
  initiatedAt: string | null;
  paidAt: string | null;
}

export interface VendorPayoutDetail extends VendorPayoutSummary {
  lines: Array<{
    payoutLineId: string;
    settlementId: string;
    glpOrderId: string | null;
    location: { storeId: string; name: string };
    grossSalesCents: number;
    commissionCents: number;
    adjustmentCents: number;
    netCents: number;
  }>;
  adjustments: Array<{
    adjustmentId: string;
    reasonCode: string;
    responsibility: string;
    amountCents: number | null;
    state: string;
    reviewedAt: string | null;
    reviewNote: string | null;
    appealedAt: string | null;
    appealText: string | null;
    appealDecision: string | null;
  }>;
}

export interface VendorAdjustment {
  adjustmentId: string;
  glpOrderId: string | null;
  location: { storeId: string; name: string } | null;
  reasonCode: string;
  responsibility: string;
  proposedAmountCents: number;
  approvedAmountCents: number | null;
  state: string;
  evidence: unknown;
  reviewNote: string | null;
  reviewedAt: string | null;
  appealedAt: string | null;
  appealText: string | null;
  appealDecision: string | null;
  createdAt: string;
  canAppeal: boolean;
}

function qs(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

/** Read a File as raw base64 (no data: prefix) for the assets upload endpoint. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export const vendorApiClient = {
  getCapabilities: () => api.get<MeCapabilities>('/vendor/me/capabilities'),

  getDashboardPreferences: (vendorId: string) =>
    api.get<DashboardPreferences>(`/vendor/${vendorId}/dashboard/preferences`),
  saveDashboardPreferences: (vendorId: string, input: Pick<DashboardPreferences, 'order' | 'hidden'>) =>
    api.put<DashboardPreferences>(`/vendor/${vendorId}/dashboard/preferences`, input),

  // ── Application (applicant — pre-membership) ─────────────────────
  // Submitting is NOT here: applications are created on the public site
  // (/vendors/apply → IAM-signed POST /vendor/public/applications), which
  // provisions the vendor-pool account this portal signs in with.
  getMyApplication: () => api.get<{ application: VendorApplication | null }>('/vendor/me/application'),
  editApplication: (applicationId: string, input: SubmitApplicationInput) =>
    api.patch<{ application: VendorApplication }>(`/vendor/applications/${applicationId}`, input),
  withdrawApplication: (applicationId: string) =>
    api.post<{ application: VendorApplication }>(`/vendor/applications/${applicationId}/withdraw`),

  getProfile: (vendorId: string) => api.get<VendorProfile>(`/vendor/${vendorId}/profile`),
  updateProfile: (vendorId: string, input: UpdateProfileInput) =>
    api.put<VendorProfile>(`/vendor/${vendorId}/profile`, input),

  /** Upload an image through the vendor-api → S3; returns the CDN URL. */
  uploadAsset: async (vendorId: string, kind: AssetKind, file: File) => {
    const dataBase64 = await fileToBase64(file);
    return api.post<{ cdnUrl: string }>(`/vendor/${vendorId}/assets`, {
      kind,
      contentType: file.type,
      dataBase64,
    });
  },

  // ── Products ─────────────────────────────────────────────────────
  listProducts: (
    vendorId: string,
    opts: { q?: string; storeId?: string; status?: ProductFilter; department?: string; page?: number; pageSize?: number } = {},
  ) =>
    api.get<ProductsListResponse>(
      `/vendor/${vendorId}/products${qs({
        q: opts.q,
        storeId: opts.storeId,
        status: opts.status === 'all' ? undefined : opts.status,
        department: opts.department,
        page: opts.page,
        pageSize: opts.pageSize,
      })}`,
    ),
  getProductsSummary: (vendorId: string) =>
    api.get<{ total: number; needsAnswer: number }>(`/vendor/${vendorId}/products/summary`),
  getProduct: (vendorId: string, vendorProductId: string) =>
    api.get<ProductDetail>(`/vendor/${vendorId}/products/${vendorProductId}`),
  saveProductPrices: (vendorId: string, vendorProductId: string, locations: LocationPriceInput[]) =>
    api.put<ProductDetail>(`/vendor/${vendorId}/products/${vendorProductId}/prices`, { locations }),
  bulkUpdateProducts: (
    vendorId: string,
    input: { storeId: string; vendorProductIds: string[]; action: BulkAction; saleStartsOn?: string | null; saleEndsOn?: string | null },
  ) => api.post<BulkResult>(`/vendor/${vendorId}/products/bulk`, input),
  listPromotionSales: (
    vendorId: string,
    opts: { q?: string; storeId?: string; ending?: SaleEndingFilter; page?: number; pageSize?: number } = {},
  ) =>
    api.get<PromotionSalesResponse>(
      `/vendor/${vendorId}/promotions/sales${qs({
        q: opts.q,
        storeId: opts.storeId,
        ending: opts.ending === 'any' ? undefined : opts.ending,
        page: opts.page,
        pageSize: opts.pageSize,
      })}`,
    ),
  getCircularConfig: (vendorId: string) => api.get<CircularConfig>(`/vendor/${vendorId}/circulars/config`),
  listCircularIssues: (vendorId: string) => api.get<{ issues: CircularIssueSummary[] }>(`/vendor/${vendorId}/circulars/issues`),
  getCircularIssue: (vendorId: string, issueId: string) => api.get<{ issue: CircularIssueDetail; locked: boolean }>(`/vendor/${vendorId}/circulars/issues/${issueId}`),
  createCircularIssue: (vendorId: string, input: { publicationDate: string; businessTimezone: string; templateKey: string; locations: { kind: 'all' } | { kind: 'selected'; storeIds: string[] } }) =>
    api.post<CircularIssueSummary & { carriedOffers: number }>(`/vendor/${vendorId}/circulars/issues`, input),
  updateCircularIssue: (vendorId: string, issueId: string, input: { templateKey?: string; locations?: { kind: 'all' } | { kind: 'selected'; storeIds: string[] } }) =>
    api.patch<{ issue: CircularIssueSummary }>(`/vendor/${vendorId}/circulars/issues/${issueId}`, input),
  intakeCircularPromotions: (vendorId: string, source: 'manual' | 'xlsx', rows: CircularPromotionInput[]) =>
    api.post<{ rows: Array<{ promotionId: string; issuesAdded: number }> }>(`/vendor/${vendorId}/circulars/promotions/intake`, { source, rows }),
  updateCircularOffer: (vendorId: string, issueId: string, issueOfferId: string, input: { displaySelected?: boolean; featured?: boolean; sortOrder?: number; productImages?: Array<{ vendorProductId: string; imageId: string | null }> }) =>
    api.patch(`/vendor/${vendorId}/circulars/issues/${issueId}/offers/${issueOfferId}`, input),
  submitCircularIssue: (vendorId: string, issueId: string) => api.post<{ issue: CircularIssueSummary }>(`/vendor/${vendorId}/circulars/issues/${issueId}/submit`),
  listCircularImages: (vendorId: string, vendorProductId?: string) => api.get<{ images: CircularImage[] }>(`/vendor/${vendorId}/circulars/images${qs({ vendorProductId })}`),
  uploadCircularImage: async (vendorId: string, vendorProductId: string, file: File) => {
    const prepared = await api.post<{ imageId: string; uploadUrl: string; contentType: string }>(`/vendor/${vendorId}/circulars/images/prepare`, {
      vendorProductId, filename: file.name, contentType: file.type, byteSize: file.size,
    });
    const put = await fetch(prepared.uploadUrl, { method: 'PUT', headers: { 'Content-Type': prepared.contentType }, body: file });
    if (!put.ok) throw new VendorApiError(`Image upload failed (${put.status})`, put.status);
    return api.post<{ imageId: string; status: string; uploadedAt: string }>(`/vendor/${vendorId}/circulars/images/${prepared.imageId}/confirm`);
  },
  getReportsOverview: (vendorId: string, storeId?: string) =>
    api.get<ReportsOverview>(`/vendor/${vendorId}/reports/overview${qs({ storeId })}`),

  // ── Orders and payouts ──────────────────────────────────────────
  listOrders: (vendorId: string, opts: { storeId?: string; status?: VendorOrderPhase | 'all'; q?: string; page?: number; pageSize?: number } = {}) =>
    api.get<VendorOrdersResponse>(`/vendor/${vendorId}/orders${qs({ storeId: opts.storeId, status: opts.status, q: opts.q, page: opts.page, pageSize: opts.pageSize })}`),
  getOrder: (vendorId: string, orderLocationId: string) =>
    api.get<{ order: VendorOrderDetail }>(`/vendor/${vendorId}/orders/${orderLocationId}`),
  startOrderPrep: (vendorId: string, orderLocationId: string) =>
    api.post(`/vendor/${vendorId}/orders/${orderLocationId}/start-prep`),
  checkoffOrderItem: (vendorId: string, orderLocationId: string, orderItemId: string, checked: boolean) =>
    api.post(`/vendor/${vendorId}/orders/${orderLocationId}/items/${orderItemId}/checkoff`, { checked, method: 'manual', idempotencyKey: crypto.randomUUID() }),
  scanOrderItem: (vendorId: string, orderLocationId: string, barcode: string) =>
    api.post<{ orderItemId: string; checked: true; method: 'barcode'; unchanged: boolean }>(`/vendor/${vendorId}/orders/${orderLocationId}/scan`, { barcode, idempotencyKey: crypto.randomUUID() }),
  reportOrderItemUnavailable: (vendorId: string, orderLocationId: string, orderItemId: string, note?: string) =>
    api.post(`/vendor/${vendorId}/orders/${orderLocationId}/items/${orderItemId}/unavailable`, { note, idempotencyKey: crypto.randomUUID() }),
  uploadOrderSeal: async (vendorId: string, orderLocationId: string, file: File) =>
    api.post<{ evidenceId: string; type: string; url: string; capturedAt: string }>(`/vendor/${vendorId}/orders/${orderLocationId}/seal-evidence`, {
      contentType: file.type,
      dataBase64: await fileToBase64(file),
    }),
  markOrderReady: (vendorId: string, orderLocationId: string, bagCount: number, sealEvidenceId: string) =>
    api.post(`/vendor/${vendorId}/orders/${orderLocationId}/ready`, { bagCount, sealEvidenceId }),
  markOrderHandedOff: (vendorId: string, orderLocationId: string) =>
    api.post(`/vendor/${vendorId}/orders/${orderLocationId}/handoff`),
  downloadOrderPrepPdf: (vendorId: string, orderLocationId: string) =>
    download(`/vendor/${vendorId}/orders/${orderLocationId}/prep-pdf`),
  downloadOrderPrepPdfs: (vendorId: string, orderLocationIds: string[]) =>
    download(`/vendor/${vendorId}/orders/prep-pdf${qs({ orderLocationIds: orderLocationIds.join(',') })}`),
  downloadOrderLabelsPdf: (vendorId: string, orderLocationId: string, bags?: number) =>
    download(`/vendor/${vendorId}/orders/${orderLocationId}/labels-pdf${qs({ bags })}`),
  listPayouts: (vendorId: string, page = 1, pageSize = 25) =>
    api.get<{ page: number; pageSize: number; total: number; items: VendorPayoutSummary[] }>(`/vendor/${vendorId}/payouts${qs({ page, pageSize })}`),
  getPayout: (vendorId: string, payoutId: string) =>
    api.get<{ payout: VendorPayoutDetail }>(`/vendor/${vendorId}/payouts/${payoutId}`),
  downloadPayoutCsv: (vendorId: string, payoutId: string) =>
    download(`/vendor/${vendorId}/payouts/${payoutId}/csv`),
  listAdjustments: (vendorId: string) =>
    api.get<{ items: VendorAdjustment[] }>(`/vendor/${vendorId}/adjustments`),
  appealAdjustment: async (vendorId: string, adjustmentId: string, text: string, attachment?: File) =>
    api.post<{ adjustmentId: string; state: string; appealedAt: string }>(`/vendor/${vendorId}/adjustments/${adjustmentId}/appeal`, {
      text,
      ...(attachment ? { attachment: { fileName: attachment.name, contentType: attachment.type, dataBase64: await fileToBase64(attachment) } } : {}),
    }),
  updateProduct: (vendorId: string, vendorProductId: string, input: { vendorSku?: string | null; spanish?: ProductSpanishInput }) =>
    api.patch<ProductDetail>(`/vendor/${vendorId}/products/${vendorProductId}`, input),
  createChangeRequest: (
    vendorId: string,
    vendorProductId: string,
    input: { field: ChangeField; proposedValue?: string; reason?: string; attachmentUrl?: string },
  ) => api.post<ChangeRequest>(`/vendor/${vendorId}/products/${vendorProductId}/change-requests`, input),
  /** A product our staff sent back goes to them again, with the vendor's reply. */
  resubmitProduct: (vendorId: string, vendorProductId: string, reply?: string) =>
    api.post<{ reviewStatus: 'pending' }>(`/vendor/${vendorId}/products/${vendorProductId}/resubmit`, reply ? { reply } : {}),
  withdrawChangeRequest: (vendorId: string, requestId: string) =>
    api.post<ChangeRequest>(`/vendor/${vendorId}/change-requests/${requestId}/withdraw`),

  createProducts: (vendorId: string, input: CreateProductsInput) =>
    api.post<{ items: CreatedProduct[] }>(`/vendor/${vendorId}/products`, input),

  // ── Questions about a product ────────────────────────────────────
  listQuestions: (vendorId: string, opts: { page?: number; pageSize?: number } = {}) =>
    api.get<QuestionsListResponse>(`/vendor/${vendorId}/questions${qs({ page: opts.page, pageSize: opts.pageSize })}`),
  answerQuestion: (vendorId: string, questionId: string, answer: 'yes' | 'no') =>
    api.post<AnswerResult>(`/vendor/${vendorId}/questions/${questionId}/answer`, { answer }),
  handOffProduct: (vendorId: string, vendorProductId: string) =>
    api.post<{ vendorProductId: string; blockState: ProductBlock['state'] | null }>(`/vendor/${vendorId}/products/${vendorProductId}/hand-off`),
  takeBackProduct: (vendorId: string, vendorProductId: string) =>
    api.post<{ vendorProductId: string; blockState: ProductBlock['state'] | null }>(`/vendor/${vendorId}/products/${vendorProductId}/take-back`),
  reopenProduct: (vendorId: string, vendorProductId: string, input: ReopenInput) =>
    api.post<{ vendorProductId: string; blockState: ProductBlock['state'] | null }>(`/vendor/${vendorId}/products/${vendorProductId}/reopen`, input),
  /** Tells us what a product was missing; it goes live with the last answer. */
  supplyMissing: (vendorId: string, vendorProductId: string, input: SupplyInput) =>
    api.post<{ vendorProductId: string; blockState: ProductBlock['state'] | null; remaining: MissingField[] }>(
      `/vendor/${vendorId}/products/${vendorProductId}/supply`,
      input,
    ),

  // ── Spreadsheet import ───────────────────────────────────────────
  previewImport: (vendorId: string, sheets: ImportSheetInput[]) => api.post<ImportPreview>(`/vendor/${vendorId}/imports/preview`, { sheets }),
  createImport: (
    vendorId: string,
    input: {
      sheets: Array<ImportSheetInput & { mapping: ImportMapping; location: SheetLocation }>;
      blanks: ImportBlanks;
      photos: Array<{ name: string; url: string }>;
      /** The spreadsheet files themselves, kept as the backup of this import. */
      originals?: Array<{ name: string; bytes: number }>;
    },
  ) => api.post<{ batchId: string; rowCount: number; originals?: ImportOriginalUpload[] }>(`/vendor/${vendorId}/imports`, input),
  confirmImportOriginal: (vendorId: string, batchId: string, fileId: string) =>
    api.post<{ stored: true }>(`/vendor/${vendorId}/imports/${batchId}/originals/${fileId}/confirm`),
  addImportRows: (vendorId: string, batchId: string, piece: RowPiece) =>
    api.post<{ received: number; expected: number }>(`/vendor/${vendorId}/imports/${batchId}/rows`, piece),
  startImport: (vendorId: string, batchId: string) => api.post<ImportStatus>(`/vendor/${vendorId}/imports/${batchId}/start`),
  getImport: (vendorId: string, batchId: string) => api.get<ImportStatus>(`/vendor/${vendorId}/imports/${batchId}`),
  listImportRows: (vendorId: string, batchId: string, opts: { outcome: ImportOutcome; page?: number; pageSize?: number; cells?: boolean }) =>
    api.get<ImportRowsResponse>(
      `/vendor/${vendorId}/imports/${batchId}/rows${qs({ outcome: opts.outcome, page: opts.page, pageSize: opts.pageSize, cells: opts.cells ? '1' : undefined })}`,
    ),

  /** The language this person runs the portal in — emails to them are written in it. */
  setLanguage: (language: 'en' | 'es') => api.put<{ language: string; stored: boolean }>('/vendor/me/language', { language }),

  listTeam: (vendorId: string) =>
    api.get<{ count: number; items: TeamMember[] }>(`/vendor/${vendorId}/team`),
  addMember: (vendorId: string, input: AddMemberInput) =>
    api.post<{ membershipId: string; role: VendorRole; vendorUserId: string; invited: boolean }>(
      `/vendor/${vendorId}/team`,
      input,
    ),
  updateMember: (vendorId: string, membershipId: string, input: { role: VendorRole }) =>
    api.patch(`/vendor/${vendorId}/team/${membershipId}`, input),
  removeMember: (vendorId: string, membershipId: string) =>
    api.del(`/vendor/${vendorId}/team/${membershipId}`),

  listSupportContacts: (vendorId: string) =>
    api.get<{ count: number; items: SupportContact[] }>(`/vendor/${vendorId}/support-contacts`),
  createSupportContact: (vendorId: string, input: Omit<SupportContact, 'contactId' | 'createdAt' | 'updatedAt'>) =>
    api.post<SupportContact>(`/vendor/${vendorId}/support-contacts`, input),
  updateSupportContact: (vendorId: string, contactId: string, input: Partial<Omit<SupportContact, 'contactId' | 'createdAt' | 'updatedAt'>>) =>
    api.patch<SupportContact>(`/vendor/${vendorId}/support-contacts/${contactId}`, input),
  archiveSupportContact: (vendorId: string, contactId: string) =>
    api.del<{ contactId: string; archived: true }>(`/vendor/${vendorId}/support-contacts/${contactId}`),

  listOwnershipRequests: (vendorId: string) =>
    api.get<{ count: number; items: OwnershipRequest[] }>(`/vendor/${vendorId}/ownership-requests`),
  createPortalOwnerTransfer: (vendorId: string, targetVendorUserId: string, reason: string) =>
    api.post<OwnershipRequest>(`/vendor/${vendorId}/ownership-requests`, { requestType: 'portal_owner_transfer', targetVendorUserId, reason }),
  createLegalOwnershipChange: (vendorId: string, input: { legalBusinessName: string; proposedOwnerName: string; proposedOwnerEmail: string; reason: string }) =>
    api.post<OwnershipRequest>(`/vendor/${vendorId}/ownership-requests`, { requestType: 'legal_ownership_change', ...input }),
  cancelOwnershipRequest: (vendorId: string, requestId: string) =>
    api.post<{ requestId: string; cancelled: true }>(`/vendor/${vendorId}/ownership-requests/${requestId}/cancel`),

  submitHelpRequest: (vendorId: string, input: { category: 'onboarding' | 'orders' | 'products' | 'locations' | 'payouts' | 'technical' | 'urgent' | 'other'; subject: string; message: string; storeId?: string; callbackRequested?: boolean; locale?: 'en' | 'es' }) =>
    api.post<{ requestId: string; submittedAt: string; emailAlternative: string }>(`/vendor/${vendorId}/help/requests`, input),

  // ── Locations (Phase 1) ──────────────────────────────────────────
  listLocations: (vendorId: string, opts: { status?: string; q?: string } = {}) =>
    api.get<LocationsListResponse>(`/vendor/${vendorId}/locations${qs({ status: opts.status, q: opts.q })}`),
  getLocation: (vendorId: string, storeId: string) => api.get<VendorLocation>(`/vendor/${vendorId}/locations/${storeId}`),
  createLocation: (vendorId: string, input: CreateLocationInput) =>
    api.post<VendorLocation>(`/vendor/${vendorId}/locations`, input),
  updateLocation: (vendorId: string, storeId: string, input: UpdateLocationInput) =>
    api.patch<VendorLocation>(`/vendor/${vendorId}/locations/${storeId}`, input),
  /** Rows of a locations file. `check` reads them and reports problems without adding anything. */
  importLocations: (vendorId: string, input: { headers: string[]; rows: Array<{ r: number; c: Cell[] }>; check?: boolean }) =>
    api.post<{ results: LocationRowResult[] }>(`/vendor/${vendorId}/locations/import`, input),
  getLocationRecipients: (vendorId: string, storeId: string) =>
    api.get<LocationRecipients>(`/vendor/${vendorId}/locations/${storeId}/recipients`),
  saveLocationRecipients: (vendorId: string, storeId: string, emailType: RecipientEmailType, people: RecipientInput[]) =>
    api.put<{ emailType: RecipientEmailType; people: Array<Omit<LocationRecipient, 'emailType'>> }>(
      `/vendor/${vendorId}/locations/${storeId}/recipients`,
      { emailType, people },
    ),
  resubmitLocation: (vendorId: string, storeId: string) =>
    api.post<VendorLocation>(`/vendor/${vendorId}/locations/${storeId}/resubmit`),
  retireLocation: (vendorId: string, storeId: string, reason?: string) =>
    api.post<{ storeId: string; archived: boolean }>(`/vendor/${vendorId}/locations/${storeId}/archive`, reason ? { reason } : {}),
  listLocationArchives: (vendorId: string) =>
    api.get<{ count: number; items: LocationArchive[] }>(`/vendor/${vendorId}/locations/archives`),
  getCertificationTypes: (vendorId: string) =>
    api.get<{ items: CertificationType[]; proofRequired: true; maxBytes: number }>(`/vendor/${vendorId}/certifications/types`),
  listCertifications: (vendorId: string, storeId: string) =>
    api.get<{ count: number; items: VendorCertification[] }>(`/vendor/${vendorId}/locations/${storeId}/certifications`),
  submitCertification: async (vendorId: string, storeId: string, input: { certificationKey: string; customLabel?: string; issuerName: string; certificateRef?: string; issuedOn?: string; expiresOn: string; file: File }) =>
    api.post<VendorCertification>(`/vendor/${vendorId}/locations/${storeId}/certifications`, {
      certificationKey: input.certificationKey,
      ...(input.customLabel ? { customLabel: input.customLabel } : {}),
      issuerName: input.issuerName,
      ...(input.certificateRef ? { certificateRef: input.certificateRef } : {}),
      ...(input.issuedOn ? { issuedOn: input.issuedOn } : {}),
      expiresOn: input.expiresOn,
      fileName: input.file.name,
      contentType: input.file.type,
      contentBase64: await fileToBase64(input.file),
    }),
  archiveCertification: (vendorId: string, storeId: string, certificationId: string, reason?: string) =>
    api.post<{ certificationId: string; archived: true }>(`/vendor/${vendorId}/locations/${storeId}/certifications/${certificationId}/archive`, reason ? { reason } : {}),

  listRecalls: (vendorId: string) =>
    api.get<{ count: number; items: VendorRecall[] }>(`/vendor/${vendorId}/recalls`),
};
